import type { EventCategory, EventDetail, EventUpdateInput } from "@cdt/shared";
import type { UseFormReturn } from "react-hook-form";
import { z } from "zod";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { isoToLocalInput, localInputToIso } from "@/lib/dateInput";

// The form works with plain strings because `<input>` and friends expose their
// state that way. We convert to the backend-shaped EventUpdateInput only on
// submit, using the dirtyFields map to send a minimal patch.
export const ReviewFormSchema = z.object({
  title: z.string(),
  description: z.string(),
  startDate: z.string(),
  endDate: z.string(),
  location: z.string(),
  organizer: z.string(),
  categoryId: z.string(),
});

export type ReviewFormValues = z.infer<typeof ReviewFormSchema>;

/**
 * Build the initial form state from an event row. Nulls become empty strings
 * so controlled inputs stay defined; dates are rendered in Europe/Zurich for
 * the datetime-local widget.
 */
export function defaultsFromEvent(event: EventDetail): ReviewFormValues {
  return {
    title: event.title ?? "",
    description: event.description ?? "",
    startDate: isoToLocalInput(event.startDate),
    endDate: isoToLocalInput(event.endDate),
    location: event.location ?? "",
    organizer: event.organizer ?? "",
    categoryId: event.categoryId ?? "",
  };
}

/**
 * Translate a set of (potentially only partially dirty) form values into
 * the patch payload expected by `PATCH /api/events/:id`. Only dirty fields
 * are included so the backend can reason about "what actually changed".
 */
export function buildPatchFromForm(
  values: ReviewFormValues,
  dirty: Partial<Record<keyof ReviewFormValues, boolean>>,
): EventUpdateInput {
  const patch: EventUpdateInput = {};

  if (dirty.title) patch.title = values.title.trim();

  if (dirty.description) {
    const t = values.description.trim();
    patch.description = t.length > 0 ? t : null;
  }

  if (dirty.location) {
    const t = values.location.trim();
    patch.location = t.length > 0 ? t : null;
  }

  if (dirty.organizer) {
    const t = values.organizer.trim();
    patch.organizer = t.length > 0 ? t : null;
  }

  if (dirty.categoryId) {
    const t = values.categoryId.trim();
    patch.categoryId = t.length > 0 ? t : null;
  }

  if (dirty.startDate) {
    patch.startDate =
      values.startDate.length > 0 ? localInputToIso(values.startDate) : null;
  }

  if (dirty.endDate) {
    patch.endDate =
      values.endDate.length > 0 ? localInputToIso(values.endDate) : null;
  }

  return patch;
}

interface ReviewFormProps {
  form: UseFormReturn<ReviewFormValues>;
  categories: EventCategory[];
  disabled?: boolean;
}

export function ReviewForm({
  form,
  categories,
  disabled = false,
}: ReviewFormProps) {
  const { register, formState } = form;
  const errors = formState.errors;

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <Field
        label="Titolo"
        error={errors.title?.message}
        className="md:col-span-2"
      >
        <Input
          {...register("title")}
          placeholder="Titolo dell'evento"
          disabled={disabled}
        />
      </Field>

      <Field
        label="Descrizione"
        error={errors.description?.message}
        className="md:col-span-2"
      >
        <Textarea
          {...register("description")}
          rows={3}
          placeholder="Descrizione breve"
          disabled={disabled}
        />
      </Field>

      <Field label="Inizio" error={errors.startDate?.message}>
        <Input
          type="datetime-local"
          {...register("startDate")}
          disabled={disabled}
        />
      </Field>

      <Field label="Fine" error={errors.endDate?.message}>
        <Input
          type="datetime-local"
          {...register("endDate")}
          disabled={disabled}
        />
      </Field>

      <Field label="Luogo" error={errors.location?.message}>
        <Input
          {...register("location")}
          placeholder="Sede / citta'"
          disabled={disabled}
        />
      </Field>

      <Field label="Organizzatore" error={errors.organizer?.message}>
        <Input
          {...register("organizer")}
          placeholder="Chi organizza"
          disabled={disabled}
        />
      </Field>

      <Field
        label="Categoria"
        error={errors.categoryId?.message}
        className="md:col-span-2"
      >
        <select
          {...register("categoryId")}
          disabled={disabled}
          className="h-10 w-full rounded-md border bg-background px-3 text-sm"
        >
          <option value="">Nessuna categoria</option>
          {categories.map((category) => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </select>
      </Field>
    </div>
  );
}

interface FieldProps {
  label: string;
  error?: string | undefined;
  className?: string;
  children: React.ReactNode;
}

function Field({ label, error, className, children }: FieldProps) {
  return (
    <div className={className}>
      <Label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </Label>
      {children}
      {error ? (
        <p className="mt-1 text-xs text-destructive">{error}</p>
      ) : null}
    </div>
  );
}
